"use client";

import { useEffect, useRef, useState } from "react";
import { safeAssistantLink } from "@/app/libs/assistant/safeLink";
import { usePathname, useSearchParams } from "next/navigation";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

const GREETING =
  "Hi! Ask us anything about courses, fun dives or liveaboards — dates, prices, what's included, or which course fits you.";

/** Renders the assistant's [label](url) links and **bold** spans. */
function renderContent(text: string) {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    const href = link ? safeAssistantLink(link[2]) : null;
    if (link && href) {
      return (
        <a
          key={i}
          href={href}
          target="_blank"
          rel="noreferrer"
          className="text-cyan-700 font-semibold underline underline-offset-2"
        >
          {link[1]}
        </a>
      );
    }
    const bold = part.match(/^\*\*([^*]+)\*\*$/);
    if (bold) {
      return (
        <strong key={i} className="font-semibold">
          {bold[1]}
        </strong>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

const STORAGE_KEY = "buw_assistant_chat";
const CID_KEY = "buw_assistant_cid";
const VIEWED_KEY = "buw_assistant_viewed";
const SOURCE_KEY = "buw_assistant_source";
const MAX_VIEWED = 5;

/** Listing and boat pages are the ones worth remembering for the chat. */
function isItemPage(path: string): boolean {
  return /^\/(listings|liveaboards)\/[^/]+/.test(path);
}

/** Item pages seen in this tab, most recent first. */
function rememberViewed(path: string): string[] {
  try {
    const raw = sessionStorage.getItem(VIEWED_KEY);
    const list: string[] = raw ? JSON.parse(raw) : [];
    const next = isItemPage(path)
      ? [path, ...list.filter((p) => p !== path)].slice(0, MAX_VIEWED)
      : list;
    sessionStorage.setItem(VIEWED_KEY, JSON.stringify(next));
    return next;
  } catch {
    return isItemPage(path) ? [path] : [];
  }
}

/** Where the tab first arrived from (Google, an ad, a link...), kept once. */
function arrivalSource(): string {
  try {
    const saved = sessionStorage.getItem(SOURCE_KEY);
    if (saved !== null) return saved;
    const params = new URLSearchParams(window.location.search);
    let referrer = "";
    try {
      const host = document.referrer ? new URL(document.referrer).host : "";
      if (host && host !== window.location.host) referrer = host;
    } catch {
      // Unparseable referrer — leave it out.
    }
    const parts = [
      referrer && `referrer ${referrer}`,
      params.get("utm_source") && `utm_source ${params.get("utm_source")}`,
      params.get("utm_medium") && `utm_medium ${params.get("utm_medium")}`,
      params.get("utm_campaign") && `utm_campaign ${params.get("utm_campaign")}`,
      `landed on ${window.location.pathname}`,
    ].filter(Boolean);
    const source = parts.join(", ").slice(0, 200);
    sessionStorage.setItem(SOURCE_KEY, source);
    return source;
  } catch {
    return "";
  }
}
// A conversation with no messages for this long starts over.
const IDLE_MS = 2 * 60 * 1000;

function newConversationId(): string {
  const cid = Math.random().toString(36).slice(2) + Date.now().toString(36);
  try {
    sessionStorage.setItem(CID_KEY, cid);
  } catch {
    // Best effort only.
  }
  return cid;
}

function loadSaved(): { open: boolean; messages: ChatMessage[]; lastActive: number; needsEmail?: boolean; emailSent?: boolean } {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const lastActive = Number(parsed?.lastActive) || 0;
      if (Array.isArray(parsed?.messages) && Date.now() - lastActive < IDLE_MS) {
        return { open: !!parsed.open, messages: parsed.messages, lastActive, needsEmail: !!parsed.needsEmail, emailSent: !!parsed.emailSent };
      }
    }
  } catch {
    // Private mode etc. — start fresh.
  }
  return { open: false, messages: [], lastActive: 0 };
}

const ChatAssistant: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [restored, setRestored] = useState(false);
  const [needsEmail, setNeedsEmail] = useState(false);
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const convIdRef = useRef<string>("");
  const lastActiveRef = useRef<number>(0);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Survive navigation: the widget remounts on every page, so the
  // conversation lives in sessionStorage for the tab's lifetime.
  useEffect(() => {
    const saved = loadSaved();
    setMessages(saved.messages);
    setOpen(saved.open);
    setNeedsEmail(!!(saved as any).needsEmail);
    setEmailSent(!!(saved as any).emailSent);
    lastActiveRef.current = saved.lastActive;
    let cid: string | null = null;
    try {
      // An expired conversation also gets a fresh id.
      if (saved.messages.length) cid = sessionStorage.getItem(CID_KEY);
    } catch {
      // Best effort only.
    }
    convIdRef.current = cid || newConversationId();
    setRestored(true);
  }, []);

  useEffect(() => {
    if (!restored) return;
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          open,
          messages,
          needsEmail,
          emailSent,
          lastActive: lastActiveRef.current,
        })
      );
    } catch {
      // Best effort only.
    }
  }, [open, messages, needsEmail, emailSent, restored]);

  // Clear the conversation once it has sat idle, even with the page open.
  useEffect(() => {
    if (!messages.length || busy) return;
    const timer = setTimeout(() => {
      setMessages([]);
      setNeedsEmail(false);
      setEmailSent(false);
      setEmail("");
      convIdRef.current = newConversationId();
    }, Math.max(0, lastActiveRef.current + IDLE_MS - Date.now()));
    return () => clearTimeout(timer);
  }, [messages, busy]);
  const pathname = usePathname() || "/";
  const params = useSearchParams();

  // The route's tools can look a listing up by the id in the URL.
  const location = params?.get("cityOrCountry") || "";
  const pageContext = `Page: ${pathname}${location ? ` (destination searched: ${location})` : ""}`;

  // Divers often ask "is there space tomorrow?" about the listing they came
  // to from Google, so the route needs the pages behind the question.
  const viewedRef = useRef<string[]>([]);
  const sourceRef = useRef<string>("");
  useEffect(() => {
    if (!sourceRef.current) sourceRef.current = arrivalSource();
    viewedRef.current = rememberViewed(pathname);
  }, [pathname]);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight });
  }, [messages, busy, open]);

  // The admin panel doesn't need the diver chat. Hooks stay above this
  // return so the hook count doesn't change when navigating into /adm.
  if (pathname.startsWith("/adm")) return null;

  const submitEmail = async () => {
    const value = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return;
    lastActiveRef.current = Date.now();
    try {
      const response = await fetch("/api/chat-assistant", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "escalation_email",
          email: value,
          messages,
          conversationId: convIdRef.current,
          pageContext,
          viewedPages: viewedRef.current,
          source: sourceRef.current,
        }),
      });
      if (!response.ok || !(await response.json()).ok) return;
      setEmailSent(true);
      setNeedsEmail(false);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Got it — our team will reach out to you at that email shortly.",
        },
      ]);
    } catch {
      // keep the form visible
    }
  };

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || busy) return;
    const next: ChatMessage[] = [...messages, { role: "user", content: trimmed }];
    lastActiveRef.current = Date.now();
    setMessages(next);
    setInput("");
    setBusy(true);
    try {
      const res = await fetch("/api/chat-assistant", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: next,
          pageContext,
          viewedPages: viewedRef.current,
          source: sourceRef.current,
          conversationId: convIdRef.current,
        }),
      });
      const data = await res.json();
      if ((data?.escalated || data?.limitReached) && !emailSent && !data?.emailKnown) {
        setNeedsEmail(true);
      }
      setMessages([
        ...next,
        {
          role: "assistant",
          content:
            typeof data?.reply === "string" && data.reply
              ? data.reply
              : "Sorry, something went wrong. Please try again.",
        },
      ]);
    } catch {
      setMessages([
        ...next,
        {
          role: "assistant",
          content: "Sorry, something went wrong. Please try again.",
        },
      ]);
    } finally {
      lastActiveRef.current = Date.now();
      setBusy(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-[calc(1.5rem+var(--book-bar-offset,0px))] right-6 z-40 bg-cyan-700 hover:bg-cyan-800 text-white rounded-full px-4 py-2.5 text-sm font-semibold shadow-lg transition"
        >
          ? Ask us
        </button>
      )}

      {open && (
        <div className="fixed bottom-4 right-4 left-4 sm:left-auto sm:w-[360px] z-50">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
            <div className="bg-cyan-800 text-white px-4 py-3 flex items-start justify-between">
              <div>
                <div className="font-semibold text-[14.5px]">Be Underwater</div>
                <div className="text-xs opacity-85">
                  Ask us anything about diving trips
                </div>
              </div>
              <button
                type="button"
                aria-label="Close chat"
                onClick={() => setOpen(false)}
                className="text-white/80 hover:text-white text-base leading-none px-1"
              >
                ✕
              </button>
            </div>

            <div
              ref={bodyRef}
              className="bg-slate-50 px-3 py-3 flex flex-col gap-2.5 h-[340px] overflow-y-auto"
            >
              <div className="bg-white border border-slate-200 rounded-xl rounded-bl-sm px-3 py-2 text-[13.5px] leading-relaxed max-w-[88%] self-start whitespace-pre-line">
                {GREETING}
              </div>
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={
                    m.role === "user"
                      ? "bg-cyan-700 text-white rounded-xl rounded-br-sm px-3 py-2 text-[13.5px] leading-relaxed max-w-[88%] self-end whitespace-pre-line"
                      : "bg-white border border-slate-200 rounded-xl rounded-bl-sm px-3 py-2 text-[13.5px] leading-relaxed max-w-[88%] self-start whitespace-pre-line"
                  }
                >
                  {m.role === "assistant" ? renderContent(m.content) : m.content}
                </div>
              ))}
              {busy && (
                <div className="bg-white border border-slate-200 rounded-xl rounded-bl-sm px-3 py-2 text-[13.5px] text-slate-400 self-start">
                  Typing…
                </div>
              )}
            </div>

            {needsEmail && !emailSent && (
              <div className="px-3 py-2.5 bg-cyan-50 border-t border-cyan-100">
                <p className="text-[12.5px] text-cyan-900 font-medium mb-1.5">
                  Leave your email so our team can get back to you:
                </p>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="flex-1 border border-cyan-300 rounded-full px-3.5 py-1.5 text-[13px] focus:outline-none focus:border-cyan-700"
                  />
                  <button
                    type="button"
                    onClick={submitEmail}
                    className="bg-cyan-700 hover:bg-cyan-800 text-white rounded-full px-4 py-1.5 text-[13px] font-semibold"
                  >
                    Send
                  </button>
                </div>
              </div>
            )}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send(input);
              }}
              className="flex gap-2 px-3 py-2.5 bg-white border-t border-slate-200"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                maxLength={600}
                placeholder="Type your question..."
                className="flex-1 border border-slate-300 rounded-full px-4 py-2 text-[13px] focus:outline-none focus:border-cyan-700"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                className="bg-cyan-700 hover:bg-cyan-800 disabled:opacity-40 text-white rounded-full px-4 py-2 text-[13px] font-semibold"
              >
                Send
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatAssistant;
